import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);

  // Define public routes that don't require authentication
  const publicRoutes = ['/login', '/register'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  useEffect(() => {
    if (!isLoading && !hasChecked) {
      setHasChecked(true);
      
      console.log('AuthGuard check:', {
        isAuthenticated,
        isLoading,
        pathname: location.pathname,
        isPublicRoute,
        user: user?.name
      });

      // If user is not authenticated and trying to access protected routes
      if (!isAuthenticated && !isPublicRoute) {
        console.log('Redirecting unauthenticated user to register');
        
        // Check if this is a first-time user
        const hasVisitedBefore = localStorage.getItem('hasVisitedBefore');
        const hasRegistered = localStorage.getItem('hasRegistered');
        
        // Always redirect to register for new users, login for returning users
        if (!hasVisitedBefore && !hasRegistered) {
          // First-time user - redirect to register
          localStorage.setItem('hasVisitedBefore', 'true');
          navigate('/register', { replace: true, state: { from: location } });
        } else {
          // Returning user or user who has registered before - redirect to login
          navigate('/login', { replace: true, state: { from: location } });
        }
        return;
      }

      // If user is authenticated and on auth pages, redirect to home
      if (isAuthenticated && isPublicRoute) {
        console.log('Redirecting authenticated user to home');
        navigate('/', { replace: true });
        return;
      }

      // Set visited flag for any access
      if (!localStorage.getItem('hasVisitedBefore')) {
        localStorage.setItem('hasVisitedBefore', 'true');
      }
    }
  }, [isAuthenticated, isLoading, hasChecked, location, navigate, isPublicRoute, user]);

  // Show loading while checking authentication
  if (isLoading || !hasChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-6"></div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Loading...</h2>
          <p className="text-gray-500">Checking your authentication status</p>
        </div>
      </div>
    );
  }

  // Don't render content if user needs to be redirected
  if (!isAuthenticated && !isPublicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-6"></div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Redirecting...</h2>
          <p className="text-gray-500">Please wait while we redirect you to login</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && isPublicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-6"></div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Welcome back!</h2>
          <p className="text-gray-500">Redirecting you to the main app...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthGuard;
